import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, User, Search, Building2, Paperclip, Image, FileText, MapPin, X, Smile, ChevronLeft } from 'lucide-react';
import { dayKey, dayLabel, messageTime, localSentMessage } from './chatTimeline';
import { whatsappService } from 'api/services/whatsapp.service';
import { contactsService } from 'api/services/contacts.service';
import { usersService } from 'api/services/users.service';
import { organizationsService } from 'api/services/organizations.service';
import { Contact, WhatsAppMessage, WhatsAppMessageType } from 'types';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import { useAuth } from 'hooks/useAuth';
import OrganizationFilter from 'components/Filters/OrganizationFilter';
import { FEATURE_FLAGS } from 'utils/constants';

// Media types that have a dedicated renderer below. Anything outside this set
// with no text content falls through to the friendly fallback label.
const RENDERABLE_MEDIA_TYPES = ['IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT', 'STICKER'];

const hasRenderableMedia = (message: WhatsAppMessage): boolean =>
  Boolean(message.mediaUrl) && RENDERABLE_MEDIA_TYPES.includes(message.type);

// Template messages carry their buttons in `metadata` (set at send time) so the
// chat can render button chips without a separate template lookup.
const getTemplateButtons = (message: WhatsAppMessage): Array<{ text?: string; type?: string; url?: string }> => {
  if (message.type !== 'TEMPLATE' || !message.metadata) return [];
  try {
    const meta = typeof message.metadata === 'string' ? JSON.parse(message.metadata) : message.metadata;
    const btns = meta?.buttons;
    if (Array.isArray(btns)) return btns;
    if (btns && Array.isArray(btns.buttons)) return btns.buttons; // tolerate {buttons:[...]} nesting
    return [];
  } catch {
    return [];
  }
};

// Human-readable placeholder for messages that carry no text and no renderable
// media (location, reaction, contact card, or a type WhatsApp itself couldn't
// deliver). Replaces the bare "[message]" / "UNSUPPORTED message" placeholder.
const messageFallbackLabel = (type?: string): string => {
  switch (type) {
    case 'LOCATION': return '📍 Location';
    case 'REACTION': return 'Reaction';
    case 'CONTACTS': return '👤 Contact card';
    case 'BUTTON':
    case 'INTERACTIVE': return 'Button reply';
    case 'STICKER': return 'Sticker';
    case 'IMAGE':
    case 'VIDEO':
    case 'AUDIO':
    case 'DOCUMENT': return 'Media message';
    default: return 'Unsupported message';
  }
};

// Mask a phone number for display, keeping the country-code prefix and last few
// digits visible (e.g. "+916387975661" -> "+9163*****661"). Used when a contact
// has no name so the number is shown as its identity.
const maskPhone = (phone?: string): string => {
  if (!phone || phone === 'Unknown') return 'Unknown';
  const cleaned = phone.replace(/[^\d+]/g, '');
  if (cleaned.replace('+', '').length <= 6) return cleaned;
  const visibleStart = cleaned.startsWith('+') ? 5 : 4;
  const start = cleaned.slice(0, visibleStart);
  const end = cleaned.slice(-3);
  const masked = Math.max(3, cleaned.length - visibleStart - 3);
  return `${start}${'*'.repeat(masked)}${end}`;
};

// Auto-created contacts often have their phone number as their "name". Treat a
// name that is purely a phone number as a number and mask it for display.
const looksLikePhone = (s?: string): boolean => !!s && /^\+?[\d\s-]{7,}$/.test(s.trim());

const displayContactName = (name: string | undefined, phone: string): string =>
  name ? (looksLikePhone(name) ? maskPhone(name) : name) : maskPhone(phone);


const WhatsAppChats: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const currentRole = (currentOrganization?.role || user?.role)?.toLowerCase();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [chatUsers, setChatUsers] = useState<any[]>([]);
  const [contactsPage, setContactsPage] = useState(1);
  const [hasMoreContacts, setHasMoreContacts] = useState(false);
  const [loadingMoreContacts, setLoadingMoreContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [chatFilter, setChatFilter] = useState<'all' | 'unread' | 'mine'>('all');
  const [includeAutoReplies, setIncludeAutoReplies] = useState(true);

  // Enhanced messaging state
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [messageType, setMessageType] = useState<WhatsAppMessageType>('TEXT');
  const [mediaUrl, setMediaUrl] = useState('');
  const [reactionPickerFor, setReactionPickerFor] = useState<string | null>(null);
  const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
  const [caption, setCaption] = useState('');
  const [filename, setFilename] = useState('');
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const attachmentMenuRef = useRef<HTMLDivElement>(null);

  // Organization selector
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>(
    FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA
      ? (currentOrganization?.id || '')
      : 'all'
  );

  // Determine organizationId to pass to API
  const getOrganizationId = () => {
    if (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') {
      if (selectedOrgId === 'all' || !selectedOrgId) {
        return undefined;
      }
      return selectedOrgId;
    }
    if (currentOrganization?.id && currentOrganization.id !== 'synthetic-org') {
      return currentOrganization.id;
    }
    return undefined;
  };

  // Check if organization selection is required but not selected
  const needsOrgSelection = FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA &&
                            (currentRole === 'master_admin' || currentRole === 'super_admin') &&
                            (!selectedOrgId || selectedOrgId === 'all');

  useEffect(() => {
    if (!needsOrgSelection) {
      fetchContacts();
    }
  }, [selectedOrgId, needsOrgSelection]);

  // Server-side re-query when the search text or All/Unread filter changes (debounced).
  const didMountChats = React.useRef(false);
  useEffect(() => {
    if (needsOrgSelection) return;
    if (!didMountChats.current) { didMountChats.current = true; return; } // initial load handled above
    const t = setTimeout(() => { fetchContacts(1, false); }, 350);
    return () => clearTimeout(t);
  }, [searchQuery, chatFilter, includeAutoReplies]);

  useEffect(() => {
    if (selectedContact) {
      fetchMessages(selectedContact._uid || '');
    }
  }, [selectedContact]);

  const CONTACTS_PAGE_SIZE = 20;

  const fetchContacts = async (page: number = 1, append: boolean = false) => {
    if (needsOrgSelection) {
      setLoading(false);
      return;
    }

    if (append) setLoadingMoreContacts(true); else setLoading(true);
    setError(null);

    const orgId = getOrganizationId();
    const result = await contactsService.getContacts({
      channel: 'whatsapp', // Filter to only show WhatsApp-enabled contacts
      organizationId: orgId,
      page,
      size: CONTACTS_PAGE_SIZE,
      // Most recently contacted conversations first.
      sortBy: 'lastContactedAt',
      sortDirection: 'desc',
      // Server-side chat filters (scoped to the last month of conversations).
      recentDays: 30,
      search: searchQuery.trim() || undefined,
      unreadOnly: chatFilter === 'unread' || undefined,
      // "My Leads" — only conversations assigned to the current user.
      // user.id is DECLARED number and is in fact a UUID string: users.id is
      // varchar(255) and contacts.assigned_to holds values like
      // 'd32f93f2-6b79-4646-b20a-1ab57ff8041c'. This call used to cast its whole
      // body to `any`, which is why nobody ever saw the mismatch. Correcting the
      // User type breaks Users.tsx and the mocks in 17 places and is its own
      // change; the cast is narrowed to the one field that lies, so every other
      // field on this request is type-checked against the API contract again.
      assignedTo: chatFilter === 'mine' ? (user?.id as unknown as string) : undefined,
      includeAutoReplies,
    });

    if (result.success && result.data) {
      const incoming = result.data.data;
      setContacts(prev => (append ? [...prev, ...incoming] : incoming));
      setContactsPage(page);
      setHasMoreContacts(page < (result.data.totalPages || 0));
      if (!append && incoming.length > 0 && !selectedContact) {
        setSelectedContact(incoming[0]);
      }
    } else {
      setError(result.error || 'Failed to fetch contacts');
    }

    setLoading(false);
    setLoadingMoreContacts(false);
    if (!append) {
      const ur = await whatsappService.getUnreadCounts(includeAutoReplies);
      if (ur.success && ur.data) setUnreadCounts(ur.data);
      if (chatUsers.length === 0) {
        const u = await usersService.getUsers({ perPage: 200 });
        if (u.success && u.data) setChatUsers((u.data as any).data || (u.data as any).content || []);
      }
    }
  };

  // Open a conversation and clear its unread badge (mark inbound read on the server).
  const selectConversation = (contact: Contact) => {
    setSelectedContact(contact);
    const cid = contact._uid;
    if (cid && (unreadCounts[cid] || 0) > 0) {
      setUnreadCounts(prev => ({ ...prev, [cid]: 0 }));
      whatsappService.markConversationRead(cid);
    }
  };

  const userName = (id?: string) => {
    if (!id) return '';
    const u = chatUsers.find((x) => String(x.id) === String(id));
    return u ? ([u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || u.email || id) : id;
  };

  // Somebody this conversation is assigned to who is not on this team.
  // CatchMyCall Business has conversations assigned to apps@catchmycall.ai,
  // which sits on the parent CatchMyCall org — a leftover from the org split.
  // Without this the <select> falls back to displaying its first option, so a
  // lead assigned to somebody else reads as assigned to whoever happens to sort
  // first. Saying "not on this team" is worse news and a true statement.
  const assigneeOffTeam =
    !!selectedContact?.assignedTo &&
    !chatUsers.some((u) => String(u.id) === String(selectedContact.assignedTo));

  const handleReassign = async (userId: string) => {
    if (!selectedContact?._uid) return;
    const res = await whatsappService.reassignLead(selectedContact._uid, userId);
    if (res.success) {
      const updated = { ...selectedContact, assignedTo: userId };
      setSelectedContact(updated);
      setContacts((cs) => cs.map((c) => (c._uid === updated._uid ? updated : c)));
    } else {
      alert(res.error || 'Failed to reassign');
    }
  };

  const fetchMessages = async (contactId: string) => {
    if (!contactId) return;

    const result = await whatsappService.getContactMessages(contactId);

    if (result.success && result.data) {
      // The API returns a paginated object { content: [...] }; older callers
      // expected a flat array. Accept both so messages actually render.
      const data: any = result.data;
      const list: WhatsAppMessage[] = Array.isArray(data)
        ? data
        : Array.isArray(data.content)
          ? data.content
          : [];
      // Show oldest -> newest for natural chat order.
      list.sort((a, b) => (a.sentAt || a.createdAt || 0) - (b.sentAt || b.createdAt || 0));
      setMessages(list);
    } else {
      // Reset to empty array on error
      setMessages([]);
    }
  };

  // Close attachment menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (attachmentMenuRef.current && !attachmentMenuRef.current.contains(event.target as Node)) {
        setShowAttachmentMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedContact) return;

    const phone = getWhatsAppPhone(selectedContact);
    if (!phone || phone === 'Unknown') {
      alert('Contact does not have a valid WhatsApp phone number');
      return;
    }

    if (messageType === 'TEXT' && !newMessage.trim()) return;

    setSending(true);

    const result = await whatsappService.sendMessageUniversal({
      to: phone,
      type: messageType,
      text: messageType === 'TEXT' ? newMessage : undefined,
    });

    if (result.success) {
      const sentText = newMessage;
      setMessages((msgs) => [
        ...msgs,
        localSentMessage((result.data as any)?.messageId, messageType, { content: sentText }),
      ]);
      setNewMessage('');
      // Then take the server's version — real id, and the status ticks.
      fetchMessages(selectedContact._uid || '');
    } else {
      alert(result.error || 'Failed to send message');
    }

    setSending(false);
  };

  // Send an emoji reaction to a message (and reflect it optimistically).
  const handleSendReaction = async (message: WhatsAppMessage, emoji: string) => {
    setReactionPickerFor(null);
    if (!selectedContact || !message.messageId) return;
    const phone = getWhatsAppPhone(selectedContact);
    if (!phone || phone === 'Unknown') return;
    const result = await whatsappService.sendMessageUniversal({
      to: phone,
      type: 'REACTION' as any,
      reactionMessageId: message.messageId,
      emoji,
    } as any);
    if (result.success) {
      setMessages((msgs) => msgs.map((m) => (m.id === message.id ? { ...m, reaction: emoji } : m)));
    } else {
      alert(result.error || 'Failed to send reaction');
    }
  };

  const handleSendMedia = async (type: 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'AUDIO') => {
    if (!selectedContact || !mediaUrl.trim()) return;

    const phone = getWhatsAppPhone(selectedContact);
    if (!phone || phone === 'Unknown') {
      alert('Contact does not have a valid WhatsApp phone number');
      return;
    }

    setSending(true);

    const result = await whatsappService.sendMessageUniversal({
      to: phone,
      type: type,
      mediaUrl: mediaUrl,
      caption: caption || undefined,
      filename: type === 'DOCUMENT' ? filename || undefined : undefined,
    });

    if (result.success) {
      setMessages((msgs) => [
        ...msgs,
        localSentMessage((result.data as any)?.messageId, type, {
          mediaUrl,
          content: caption || undefined,
        }),
      ]);
      setMediaUrl('');
      setCaption('');
      setFilename('');
      setShowMediaModal(false);
      setMessageType('TEXT');
      fetchMessages(selectedContact._uid || '');
    } else {
      alert(result.error || 'Failed to send media');
    }

    setSending(false);
  };

  const handleSendLocation = async () => {
    if (!selectedContact || !latitude || !longitude) return;

    const phone = getWhatsAppPhone(selectedContact);
    if (!phone || phone === 'Unknown') {
      alert('Contact does not have a valid WhatsApp phone number');
      return;
    }

    setSending(true);

    const result = await whatsappService.sendMessageUniversal({
      to: phone,
      type: 'LOCATION',
      latitude: parseFloat(latitude),
      longitude: parseFloat(longitude),
      locationName: locationName || undefined,
      locationAddress: locationAddress || undefined,
    });

    if (result.success) {
      setMessages((msgs) => [
        ...msgs,
        localSentMessage((result.data as any)?.messageId, 'LOCATION', {
          content: locationName || locationAddress || `${latitude}, ${longitude}`,
        }),
      ]);
      setLatitude('');
      setLongitude('');
      setLocationName('');
      setLocationAddress('');
      setShowLocationModal(false);
      fetchMessages(selectedContact._uid || '');
    } else {
      alert(result.error || 'Failed to send location');
    }

    setSending(false);
  };

  const openMediaModal = (type: 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'AUDIO') => {
    setMessageType(type);
    setShowMediaModal(true);
    setShowAttachmentMenu(false);
  };

  // Helper function to get WhatsApp phone number from contact
  const getWhatsAppPhone = (contact: Contact): string => {
    const waPhone = contact.phone_numbers?.find(p => p.whatsapp_enabled);
    return waPhone?.number || contact.phone_numbers?.[0]?.number || 'Unknown';
  };

  // Helper function to get WhatsApp channel info
  const getWhatsAppChannel = (contact: Contact) => {
    return contact.channels?.find(ch => ch.channel === 'whatsapp');
  };

  // Search, Unread filter, and the 1-month recency are applied SERVER-SIDE (see fetchContacts);
  // the list arrives already filtered + sorted by last message. Badge = total unread across org.
  const unreadConvoCount = Object.values(unreadCounts).filter(v => (v || 0) >= 1).length;
  const filteredContacts = contacts;

  if (loading) return <LoadingSpinner />;
  if (error && !needsOrgSelection) return <ErrorMessage message={error} onRetry={fetchContacts} />;

  return (
    // Sized to the space the shell actually leaves, so the thread runs the full
    // height of the window instead of stopping short of it.
    //
    // The numbers come from Layout, and are the only two things this has to
    // track: below lg it renders MobileHeader (h-14) and pads the page
    // `p-4 pb-20`, so 56 + 16 + 80 = 152px; at lg there is no header and the
    // padding is `p-6` both ways, so 48px.
    //
    // A column rather than a fixed height on the panel itself: the org filter
    // above is there for some roles and not others, and subtracting a guess
    // for it would leave a gap for everyone else and overflow for a role that
    // gets a taller one. As a flex child the panel simply takes what is left.
    <div className="flex flex-col gap-4 h-[calc(100dvh-9.5rem)] lg:h-[calc(100vh-3rem)]">
      {/* Organization Filter - For master_admin, super_admin, and service_admin */}
      {(currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') && (
        <div className="flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <OrganizationFilter
            value={selectedOrgId === 'all' ? '' : selectedOrgId}
            onChange={(value) => {
              if (FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA) {
                setSelectedOrgId(value || selectedOrgId);
              } else {
                setSelectedOrgId(value || 'all');
              }
            }}
            className="w-full md:w-96"
            requireSelection={FEATURE_FLAGS.REQUIRE_ORG_SELECTION_FOR_OPERATIONAL_DATA}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show friendly message when organization selection is required */}
      {needsOrgSelection && (
        <div className="flex-shrink-0 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300">Select an Organization</h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                Please select an organization from the dropdown above to view WhatsApp chats.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Chat Interface */}
      {!needsOrgSelection && <div className="flex flex-1 min-h-0 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      {/* Conversations.
          One pane at a time below md. Both panes used to render side by side at
          every width, so a 390px phone got a 130px conversation list beside a
          260px thread and neither was usable. Now the list IS the page until a
          contact is picked, and the thread takes over with a way back. */}
      <div
        className={`${selectedContact ? 'hidden md:flex' : 'flex'} w-full md:w-1/3 border-r border-gray-200 dark:border-gray-700 flex-col`}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Conversations
          </h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search contacts..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {/* All / Unread filter (WhatsApp-style) */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setChatFilter('all')}
              className={`px-3 py-1 rounded-full text-sm font-medium ${chatFilter === 'all' ? 'bg-green-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            >
              All
            </button>
            <button
              onClick={() => setChatFilter('unread')}
              className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5 ${chatFilter === 'unread' ? 'bg-green-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            >
              Unread
              {unreadConvoCount > 0 && (
                <span className={`px-1.5 rounded-full text-xs ${chatFilter === 'unread' ? 'bg-white/25' : 'bg-green-600 text-white'}`}>{unreadConvoCount}</span>
              )}
            </button>
            <button
              onClick={() => setChatFilter('mine')}
              className={`px-3 py-1 rounded-full text-sm font-medium ${chatFilter === 'mine' ? 'bg-green-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
              title="Conversations assigned to me"
            >
              My Leads
            </button>
          </div>
          {/* Auto-reply include/exclude toggle */}
          <label className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!includeAutoReplies}
              onChange={(e) => setIncludeAutoReplies(!e.target.checked)}
              className="rounded"
            />
            Hide auto-replies from unread
          </label>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredContacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
              <MessageSquare className="w-12 h-12 mb-2 opacity-50" />
              <p className="text-sm">No contacts found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredContacts.map((contact) => {
                const waChannel = getWhatsAppChannel(contact);
                const phone = getWhatsAppPhone(contact);
                return (
                  <div
                    key={contact._uid}
                    onClick={() => selectConversation(contact)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition ${
                      selectedContact?._uid === contact._uid ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        {contact.name?.[0]?.toUpperCase() || phone[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {displayContactName(contact.name, phone)}
                          </p>
                          {((unreadCounts[contact._uid || ''] ?? waChannel?.unread_count) || 0) > 0 && (
                            <span className="ml-2 px-2 py-0.5 bg-green-600 text-white rounded-full text-xs font-semibold">
                              {unreadCounts[contact._uid || ''] ?? waChannel?.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{maskPhone(phone)}</p>
                        {waChannel?.last_contact_at && (
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(waChannel.last_contact_at).toLocaleString()}
                          </p>
                        )}
                        {contact.labels && contact.labels.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {contact.labels.slice(0, 2).map((label, index) => (
                              <span
                                key={index}
                                className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs"
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {hasMoreContacts && (
                <button
                  onClick={() => fetchContacts(contactsPage + 1, true)}
                  disabled={loadingMoreContacts}
                  className="w-full py-3 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50"
                >
                  {loadingMoreContacts ? 'Loading…' : 'Load more'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`${selectedContact ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0`}>
        {selectedContact ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {/* The only way back to the list on a phone, where the list
                      is no longer on screen beside the thread. */}
                  <button
                    type="button"
                    onClick={() => setSelectedContact(null)}
                    className="md:hidden -ml-1 p-1 text-gray-500 dark:text-gray-400 shrink-0"
                    title="Back to conversations"
                    aria-label="Back to conversations"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold shrink-0">
                    {selectedContact.name?.[0]?.toUpperCase() || getWhatsAppPhone(selectedContact)[0]}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {displayContactName(selectedContact.name, getWhatsAppPhone(selectedContact))}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{maskPhone(getWhatsAppPhone(selectedContact))}</p>
                  </div>
                </div>
                {/* Lead assignee + reassign */}
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                    {!selectedContact.assignedTo
                      ? 'Unassigned'
                      : assigneeOffTeam
                        ? 'Assigned outside this team'
                        : `Assigned: ${userName(selectedContact.assignedTo)}`}
                  </span>
                  <select
                    value={selectedContact.assignedTo || ''}
                    onChange={(e) => handleReassign(e.target.value)}
                    title="Assign lead to handler"
                    className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg max-w-[140px]"
                  >
                    <option value="" disabled>Assign to…</option>
                    {assigneeOffTeam && (
                      <option value={String(selectedContact.assignedTo)} disabled>
                        Not on this team
                      </option>
                    )}
                    {chatUsers.map((u) => (
                      <option key={String(u.id)} value={String(u.id)}>
                        {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || u.email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900/50">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
                  <MessageSquare className="w-12 h-12 mb-2 opacity-50" />
                  <p className="text-sm">No messages yet</p>
                  <p className="text-xs mt-1">Start a conversation</p>
                </div>
              ) : (
                messages.map((message, i) => (
                  <React.Fragment key={message.id || `m-${i}`}>
                  {/* A separator whenever the day changes — and before the
                      first message, so the oldest one on screen is dated too. */}
                  {dayKey(message.sentAt || message.createdAt) !==
                    dayKey(messages[i - 1]?.sentAt || messages[i - 1]?.createdAt) && (
                    <div className="flex justify-center">
                      <span className="text-[11px] px-3 py-1 rounded-full bg-gray-200/80 dark:bg-gray-700/80 text-gray-600 dark:text-gray-300">
                        {dayLabel(message.sentAt || message.createdAt)}
                      </span>
                    </div>
                  )}
                  <div
                    className={`flex items-center gap-1 group ${message.direction === 'OUTBOUND' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`relative max-w-[70%] rounded-lg p-3 ${
                        message.direction === 'OUTBOUND'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white'
                      }`}
                    >
                      {message.direction === 'INBOUND' && message.autoReplyLikely && (
                        <div className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 mb-1 flex items-center gap-1">
                          🤖 Likely auto-reply
                        </div>
                      )}
                      {message.type === 'TEMPLATE' && message.mediaUrl && (
                        <img
                          src={message.mediaUrl}
                          alt=""
                          className="w-full max-h-48 object-cover rounded-lg mb-2"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      {message.content && (
                        <p className="text-sm whitespace-pre-line">{message.content}</p>
                      )}
                      {hasRenderableMedia(message) && (
                        <div className="mt-2">
                          {message.type === 'IMAGE' && (
                            <img
                              src={message.mediaUrl}
                              alt="Media"
                              className="max-w-full rounded"
                            />
                          )}
                          {message.type === 'STICKER' && (
                            <img
                              src={message.mediaUrl}
                              alt="Sticker"
                              className="max-w-[8rem] rounded"
                            />
                          )}
                          {message.type === 'VIDEO' && (
                            <video controls className="max-w-full rounded">
                              <source src={message.mediaUrl} />
                            </video>
                          )}
                          {message.type === 'AUDIO' && (
                            <audio controls className="max-w-full">
                              <source src={message.mediaUrl} />
                            </audio>
                          )}
                          {message.type === 'DOCUMENT' && (
                            <a
                              href={message.mediaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm underline inline-flex items-center gap-1"
                            >
                              <FileText className="w-4 h-4" /> Download document
                            </a>
                          )}
                        </div>
                      )}
                      {getTemplateButtons(message).length > 0 && (
                        <div className="mt-2 flex flex-col gap-1 border-t border-current/20 pt-2">
                          {getTemplateButtons(message).map((b, i) => (
                            <div
                              key={i}
                              className="text-xs text-center rounded-md px-2 py-1.5 bg-white text-blue-600 border border-gray-200 font-medium flex items-center justify-center gap-1"
                            >
                              {b.type === 'URL' && '🔗'}
                              {b.type === 'PHONE_NUMBER' && '📞'}
                              <span>{b.text || 'Button'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {!message.content && !message.mediaUrl && !hasRenderableMedia(message) && (
                        <p className="text-sm italic opacity-70">
                          {messageFallbackLabel(message.type)}
                        </p>
                      )}
                      <div
                        className={`flex items-center justify-end gap-1 mt-1 text-xs ${
                          message.direction === 'OUTBOUND' ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        <span title={message.sentAt ? new Date(message.sentAt).toLocaleString() : undefined}>
                          {messageTime(message.sentAt || message.createdAt)}
                        </span>
                        {message.direction === 'OUTBOUND' && (
                          <span>
                            {message.status === 'SENT' && '✓'}
                            {message.status === 'DELIVERED' && '✓✓'}
                            {message.status === 'READ' && '✓✓'}
                            {message.status === 'FAILED' && '✗'}
                          </span>
                        )}
                      </div>
                      {message.reaction && (
                        <span className="absolute -bottom-2.5 right-2 text-base bg-white dark:bg-gray-700 rounded-full px-1 py-0.5 leading-none shadow border border-gray-200 dark:border-gray-600">
                          {message.reaction}
                        </span>
                      )}
                    </div>
                    <div className="relative shrink-0 opacity-0 group-hover:opacity-100 transition">
                      <button
                        type="button"
                        onClick={() => setReactionPickerFor(reactionPickerFor === message.id ? null : (message.id || null))}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        title="React"
                      >
                        <Smile className="w-4 h-4" />
                      </button>
                      {reactionPickerFor === message.id && (
                        <div className="absolute z-20 bottom-full mb-1 right-0 flex gap-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full px-2 py-1 shadow-lg">
                          {REACTION_EMOJIS.map((emo) => (
                            <button key={emo} type="button" onClick={() => handleSendReaction(message, emo)} className="text-lg hover:scale-125 transition">
                              {emo}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  </React.Fragment>
                ))
              )}
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex gap-3 items-center">
                {/* Attachment Button */}
                <div className="relative" ref={attachmentMenuRef}>
                  <button
                    type="button"
                    onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
                    className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                    title="Attach media"
                  >
                    <Paperclip className="w-5 h-5" />
                  </button>

                  {/* Attachment Menu */}
                  {showAttachmentMenu && (
                    <div className="absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 min-w-[160px] z-10">
                      <button
                        type="button"
                        onClick={() => openMediaModal('IMAGE')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                      >
                        <Image className="w-4 h-4 text-blue-500" />
                        Image
                      </button>
                      <button
                        type="button"
                        onClick={() => openMediaModal('VIDEO')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                      >
                        <FileText className="w-4 h-4 text-purple-500" />
                        Video
                      </button>
                      <button
                        type="button"
                        onClick={() => openMediaModal('DOCUMENT')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                      >
                        <FileText className="w-4 h-4 text-green-500" />
                        Document
                      </button>
                      <button
                        type="button"
                        onClick={() => openMediaModal('AUDIO')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                      >
                        <FileText className="w-4 h-4 text-orange-500" />
                        Audio
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowLocationModal(true); setShowAttachmentMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
                      >
                        <MapPin className="w-4 h-4 text-red-500" />
                        Location
                      </button>
                    </div>
                  )}
                </div>

                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={sending}
                />
                <button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  className="tl-btn tl-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <>Sending...</>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send
                    </>
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
            <User className="w-16 h-16 mb-4 opacity-50" />
            <p>Select a contact to start chatting</p>
          </div>
        )}
      </div>
    </div>}

      {/* Media Send Modal */}
      {showMediaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Send {messageType.charAt(0) + messageType.slice(1).toLowerCase()}
              </h3>
              <button
                onClick={() => { setShowMediaModal(false); setMessageType('TEXT'); }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Media URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  placeholder={`Enter ${messageType.toLowerCase()} URL`}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {messageType === 'DOCUMENT' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Filename (Optional)
                  </label>
                  <input
                    type="text"
                    value={filename}
                    onChange={(e) => setFilename(e.target.value)}
                    placeholder="document.pdf"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              {(messageType === 'IMAGE' || messageType === 'VIDEO' || messageType === 'DOCUMENT') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Caption (Optional)
                  </label>
                  <input
                    type="text"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Add a caption..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => { setShowMediaModal(false); setMessageType('TEXT'); }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSendMedia(messageType as 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'AUDIO')}
                  disabled={!mediaUrl.trim() || sending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location Send Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 text-red-500" />
                Send Location
              </h3>
              <button
                onClick={() => setShowLocationModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Latitude <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    placeholder="e.g., 37.7749"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Longitude <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    placeholder="e.g., -122.4194"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Location Name (Optional)
                </label>
                <input
                  type="text"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="e.g., Golden Gate Bridge"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Address (Optional)
                </label>
                <input
                  type="text"
                  value={locationAddress}
                  onChange={(e) => setLocationAddress(e.target.value)}
                  placeholder="e.g., San Francisco, CA"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowLocationModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendLocation}
                  disabled={!latitude || !longitude || sending}
                  className="tl-btn tl-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? 'Sending...' : 'Send Location'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppChats;
